/** 視認状況報告・漁場調査等の明細（定時連絡・速報で共用）の型と変換。担当: G2 */

export interface SightingDto {
  id: number;
  sightedOn: string;
  sightedTime: string | null;
  monitoringArea: string | null;
  zoneNo: number | null;
  zoneNoCalculated: number | null;
  seaAreaClass: string | null;
  latDeg: string | null;
  latMin: string | null;
  latSec: string | null;
  lonDeg: string | null;
  lonMin: string | null;
  lonSec: string | null;
  activity: string | null;
  heading: string | null;
  nationality: string | null;
  foreignVesselName: string | null;
  foreignRegistrationNo: string | null;
  approvalNo: string | null;
  fishingType: string | null;
  vesselCount: number;
  isReported: boolean;
  hasPhoto: boolean;
  refFixedTime: boolean;
  refVoyage: boolean;
  specialNotes: string | null;
  note: string | null;
  fixedTimeReportId: number | null;
  voyageReportId: number | null;
}

export interface GearFindingDto {
  id: number;
  foundOn: string;
  foundTime: string | null;
  monitoringArea: string | null;
  zoneNo: number | null;
  zoneNoCalculated: number | null;
  seaAreaClass: string | null;
  latDeg: string | null;
  latMin: string | null;
  latSec: string | null;
  lonDeg: string | null;
  lonMin: string | null;
  lonSec: string | null;
  gearType: string | null;
  gearCount: string | null;
  nationality: string | null;
  gearFeatures: string | null;
  mmsi: string | null;
  hasPhoto: boolean;
  refFixedTime: boolean;
  refVoyage: boolean;
  specialNotes: string | null;
  fixedTimeReportId: number | null;
  voyageReportId: number | null;
}

/** フォーム上の行（数値は NumberField 用に number|null、文字列は ''） */
export interface SightingRow {
  id: number | null;
  sightedOn: string;
  sightedTime: string;
  monitoringArea: string;
  zoneNo: number | null;
  zoneNoCalculated: number | null;
  latDeg: string;
  latMin: string;
  latSec: string;
  lonDeg: string;
  lonMin: string;
  lonSec: string;
  activity: string;
  heading: string;
  nationality: string;
  foreignVesselName: string;
  foreignRegistrationNo: string;
  approvalNo: string;
  fishingType: string;
  vesselCount: number | null;
  isReported: boolean;
  hasPhoto: boolean;
  refFixedTime: boolean;
  refVoyage: boolean;
  specialNotes: string;
  note: string;
}

export interface GearRow {
  id: number | null;
  foundOn: string;
  foundTime: string;
  monitoringArea: string;
  zoneNo: number | null;
  zoneNoCalculated: number | null;
  latDeg: string;
  latMin: string;
  latSec: string;
  lonDeg: string;
  lonMin: string;
  lonSec: string;
  gearType: string;
  gearCount: string;
  nationality: string;
  gearFeatures: string;
  mmsi: string;
  hasPhoto: boolean;
  refFixedTime: boolean;
  refVoyage: boolean;
  specialNotes: string;
}

const s = (v: string | null | undefined) => v ?? '';

export const emptySighting = (date: string, ref: 'fixed' | 'voyage'): SightingRow => ({
  id: null,
  sightedOn: date,
  sightedTime: '',
  monitoringArea: '',
  zoneNo: null,
  zoneNoCalculated: null,
  latDeg: '',
  latMin: '',
  latSec: '',
  lonDeg: '',
  lonMin: '',
  lonSec: '',
  activity: '',
  heading: '',
  nationality: '',
  foreignVesselName: '',
  foreignRegistrationNo: '',
  approvalNo: '',
  fishingType: '',
  vesselCount: null,
  isReported: false,
  hasPhoto: false,
  refFixedTime: ref === 'fixed',
  refVoyage: ref === 'voyage',
  specialNotes: '',
  note: '',
});

export const emptyGear = (date: string, ref: 'fixed' | 'voyage'): GearRow => ({
  id: null,
  foundOn: date,
  foundTime: '',
  monitoringArea: '',
  zoneNo: null,
  zoneNoCalculated: null,
  latDeg: '',
  latMin: '',
  latSec: '',
  lonDeg: '',
  lonMin: '',
  lonSec: '',
  gearType: '',
  gearCount: '',
  nationality: '',
  gearFeatures: '',
  mmsi: '',
  hasPhoto: false,
  refFixedTime: ref === 'fixed',
  refVoyage: ref === 'voyage',
  specialNotes: '',
});

export const sightingFromDto = (d: SightingDto): SightingRow => ({
  ...d,
  sightedTime: s(d.sightedTime),
  monitoringArea: s(d.monitoringArea),
  latDeg: s(d.latDeg),
  latMin: s(d.latMin),
  latSec: s(d.latSec),
  lonDeg: s(d.lonDeg),
  lonMin: s(d.lonMin),
  lonSec: s(d.lonSec),
  activity: s(d.activity),
  heading: s(d.heading),
  nationality: s(d.nationality),
  foreignVesselName: s(d.foreignVesselName),
  foreignRegistrationNo: s(d.foreignRegistrationNo),
  approvalNo: s(d.approvalNo),
  fishingType: s(d.fishingType),
  specialNotes: s(d.specialNotes),
  note: s(d.note),
});

export const gearFromDto = (d: GearFindingDto): GearRow => ({
  ...d,
  foundTime: s(d.foundTime),
  monitoringArea: s(d.monitoringArea),
  latDeg: s(d.latDeg),
  latMin: s(d.latMin),
  latSec: s(d.latSec),
  lonDeg: s(d.lonDeg),
  lonMin: s(d.lonMin),
  lonSec: s(d.lonSec),
  gearType: s(d.gearType),
  gearCount: s(d.gearCount),
  nationality: s(d.nationality),
  gearFeatures: s(d.gearFeatures),
  mmsi: s(d.mmsi),
  specialNotes: s(d.specialNotes),
});

const nz = (v: string) => (v.trim() === '' ? null : v);

/** API 送信用（空文字は null、データの無い行は除外） */
export function sightingsToInput(rows: SightingRow[]) {
  return rows.map(({ zoneNoCalculated: _z, ...r }) => ({
    ...r,
    sightedOn: nz(r.sightedOn),
    sightedTime: nz(r.sightedTime),
    monitoringArea: nz(r.monitoringArea),
    latDeg: nz(r.latDeg),
    latMin: nz(r.latMin),
    latSec: nz(r.latSec),
    lonDeg: nz(r.lonDeg),
    lonMin: nz(r.lonMin),
    lonSec: nz(r.lonSec),
    activity: nz(r.activity),
    heading: nz(r.heading),
    nationality: nz(r.nationality),
    foreignVesselName: nz(r.foreignVesselName),
    foreignRegistrationNo: nz(r.foreignRegistrationNo),
    approvalNo: nz(r.approvalNo),
    fishingType: nz(r.fishingType),
    vesselCount: r.vesselCount == null ? null : String(r.vesselCount),
    specialNotes: nz(r.specialNotes),
    note: nz(r.note),
  }));
}

export function gearsToInput(rows: GearRow[]) {
  return rows.map(({ zoneNoCalculated: _z, ...r }) => ({
    ...r,
    foundOn: nz(r.foundOn),
    foundTime: nz(r.foundTime),
    monitoringArea: nz(r.monitoringArea),
    latDeg: nz(r.latDeg),
    latMin: nz(r.latMin),
    latSec: nz(r.latSec),
    lonDeg: nz(r.lonDeg),
    lonMin: nz(r.lonMin),
    lonSec: nz(r.lonSec),
    gearType: nz(r.gearType),
    gearCount: nz(r.gearCount),
    nationality: nz(r.nationality),
    gearFeatures: nz(r.gearFeatures),
    mmsi: nz(r.mmsi),
    specialNotes: nz(r.specialNotes),
  }));
}
